import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  FormControlLabel,
  LinearProgress,
  Switch,
  TextField,
  Typography,
  Chip,
  Paper,
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2/";
import EditIcon from "@mui/icons-material/Edit";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AspectRatioOutlinedIcon from "@mui/icons-material/AspectRatioOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CustomButton from "../../common/Button/CustomButton";
import FileUpload from "../../common/FileUpload";
import AddPropertyContext from "../../../context/addProperty/AddPropertyContext";

// Firebase Storage
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "../../../firebase";

// Supported aspect ratios for property photography
export const ASPECT_RATIO_OPTIONS = [
  { value: "3:2", label: "3:2", title: "Landscape", desc: "Recommended for 2-in-a-row grid", recSize: "1400 × 900 px", previewAspect: "3 / 2" },
  { value: "16:9", label: "16:9", title: "Wide Banner", desc: "Full-width hero banner", recSize: "1600 × 900 px", previewAspect: "16 / 9" },
  { value: "4:3", label: "4:3", title: "Classic", desc: "Traditional photo format", recSize: "1200 × 900 px", previewAspect: "4 / 3" },
  { value: "1:1", label: "1:1", title: "Square", desc: "Balanced square card", recSize: "1000 × 1000 px", previewAspect: "1 / 1" },
  { value: "2:1", label: "2:1", title: "Panoramic", desc: "Ultra-wide panoramic strip", recSize: "1600 × 800 px", previewAspect: "2 / 1" },
  { value: "3:4", label: "3:4", title: "Portrait", desc: "Tall vertical composition", recSize: "900 × 1200 px", previewAspect: "3 / 4" },
];

export const findNearestAspectRatio = (width, height) => {
  if (!width || !height) return "3:2";
  const ratio = width / height;
  const ratioMap = [
    { value: "2:1", num: 2 / 1 },
    { value: "16:9", num: 16 / 9 },
    { value: "3:2", num: 3 / 2 },
    { value: "4:3", num: 4 / 3 },
    { value: "1:1", num: 1 / 1 },
    { value: "3:4", num: 3 / 4 },
  ];
  let best = ratioMap[0];
  let minDiff = Math.abs(ratio - best.num);
  for (let i = 1; i < ratioMap.length; i++) {
    const diff = Math.abs(ratio - ratioMap[i].num);
    if (diff < minDiff) {
      minDiff = diff;
      best = ratioMap[i];
    }
  }
  return best.value;
};

// Convert uploaded image file to WebP format with high resolution support for both wide and tall slots
const convertImageToWebP = (
  file,
  maxWidth = 1800,
  maxHeight = 1800,
  quality = 0.90
) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const originalWidth = img.naturalWidth || img.width;
        const originalHeight = img.naturalHeight || img.height;
        const detectedAspect = findNearestAspectRatio(originalWidth, originalHeight);
        let width = originalWidth;
        let height = originalHeight;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Failed to convert image to WebP"));
              return;
            }
            const cleanBase = (file.name || "property")
              .replace(/\.[^/.]+$/, "")
              .replace(/[^\w-]/g, "_");
            const fileName = `${Date.now()}_${cleanBase}.webp`;
            const webpFile = new File([blob], fileName, { type: "image/webp" });
            resolve({ file: webpFile, fileName, detectedAspect });
          },
          "image/webp",
          quality
        );
      };
      img.onerror = (err) => reject(err);
      img.src = event.target.result;
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
};

const AddProperty = ({
  hideModal,
  handleSetProperty,
  action,
  saving = false,
  onDelete,
}) => {
  const propertyContext = useContext(AddPropertyContext);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleImageChange = (e) => {
    if (isUploading) return;
    setIsUploading(true);
    setProgress(0);
    const upImage = e.target.files[0];
    e.target.value = "";
    if (upImage) {
      convertImageToWebP(upImage, 1200, 1600, 0.88)
        .then(({ file, fileName, detectedAspect }) => {
          if (!isMounted.current) return;
          if (detectedAspect) {
            propertyContext.setState((prev) => ({
              ...prev,
              aspectRatio: detectedAspect,
            }));
          }
          handleFileUpload({
            path: "images/properties",
            file,
            fileName,
          });
        })
        .catch((ex) => {
          console.log("WebP Conversion Error:", ex);
          alert(`Error converting image to WebP: ${ex.message}`);
          setIsUploading(false);
        });
    } else {
      setIsUploading(false);
    }
  };

  const handleFileUpload = ({ path, file, fileName }) => {
    const storageRef = ref(storage, `${path}/${fileName}`);
    const metadata = { contentType: "image/webp" };
    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const prog = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setProgress(prog);
      },
      (error) => {
        setIsUploading(false);
        console.log("Upload Error:", error);
        alert(`Upload error: ${error.message}`);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref)
          .then((downloadURL) => {
            if (!isMounted.current) return;
            setIsUploading(false);
            propertyContext.setState((prev) => ({
              ...prev,
              imageUrl: downloadURL,
              imageName: fileName,
            }));
          })
          .catch((ex) => {
            setIsUploading(false);
            console.log("Error getting download URL:", ex);
            alert("Error while getting image URL");
          });
      }
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!propertyContext.state.label) {
      alert("Please provide a property label.");
      return;
    }
    const hasImage = propertyContext.state.imageUrl || propertyContext.state.image;
    if (!hasImage) {
      alert("Please upload or provide an image for the property.");
      return;
    }
    handleSetProperty(action);
  };

  const currentAccent = propertyContext.state.accent || "#10b981";
  const currentLabel = propertyContext.state.label || "Property Name";
  const currentImg = propertyContext.state.imageUrl || propertyContext.state.image;
  const currentAspectRatio = propertyContext.state.aspectRatio || "3:2";
  const selectedAspect =
    ASPECT_RATIO_OPTIONS.find((opt) => opt.value === currentAspectRatio) ||
    ASPECT_RATIO_OPTIONS[0];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#f8fafc", borderRadius: 2 } }}>
      {/* Header */}
      <Box sx={{ mb: 2.5, pb: 1.5, borderBottom: "1px solid #eef2f0" }}>
        <Typography id="property-editor-title" variant="h6" fontWeight={800} color="#1e293b">
          {action === "ADD" ? "Add Property Category" : "Edit Property Category"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure title, high-resolution photography, and aspect ratio.
        </Typography>
      </Box>

      <Grid2 container spacing={3}>
        {/* Left Column: Live Card Preview */}
        <Grid2 xs={12} md={5}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing="0.08em">
              Live Mockup Preview
            </Typography>
            <Chip
              size="small"
              label={propertyContext.state.isActive !== false ? "Active" : "Draft"}
              sx={{
                height: 18,
                fontSize: "0.62rem",
                fontWeight: 800,
                bgcolor: propertyContext.state.isActive !== false ? "#dcfce7" : "#fef3c7",
                color: propertyContext.state.isActive !== false ? "#15803d" : "#92400e",
              }}
            />
          </Box>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              bgcolor: "#f8faf9",
              borderRadius: 3,
              border: "1px dashed #d5e6e2",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              minHeight: 280,
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 320,
                aspectRatio: selectedAspect.previewAspect || "3 / 2",
                borderRadius: "16px",
                overflow: "hidden",
                position: "relative",
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                bgcolor: "#072f27",
                transition: "all 0.3s ease",
              }}
            >
              {currentImg ? (
                <Box
                  component="img"
                  src={currentImg}
                  alt={currentLabel}
                  onLoad={(e) => {
                    if (
                      e.target.naturalWidth &&
                      e.target.naturalHeight &&
                      !propertyContext.state.aspectRatio
                    ) {
                      const detected = findNearestAspectRatio(
                        e.target.naturalWidth,
                        e.target.naturalHeight
                      );
                      propertyContext.setState((prev) => ({
                        ...prev,
                        aspectRatio: detected,
                      }));
                    }
                  }}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "rgba(255,255,255,0.4)",
                    fontSize: "0.8rem",
                    p: 2,
                    textAlign: "center",
                  }}
                >
                  Upload high-resolution property photography
                </Box>
              )}

              {/* Gradient Overlay */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)",
                }}
              />

              {/* Accent Pill */}
              <Box
                sx={{
                  position: "absolute",
                  top: 12,
                  left: 12,
                  px: 1,
                  py: 0.3,
                  borderRadius: "20px",
                  bgcolor: "rgba(0,0,0,0.4)",
                  backdropFilter: "blur(6px)",
                  border: `1.5px solid ${currentAccent}66`,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: currentAccent }} />
                <Typography sx={{ color: "#fff", fontSize: "0.62rem", fontWeight: 700 }}>
                  Explore
                </Typography>
              </Box>

              {/* Aspect Ratio Pill */}
              <Box
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  px: 1.1,
                  py: 0.35,
                  borderRadius: "14px",
                  bgcolor: "rgba(0,0,0,0.55)",
                  backdropFilter: "blur(6px)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <AspectRatioOutlinedIcon sx={{ fontSize: 13, color: "#34d399" }} />
                <Typography sx={{ color: "#fff", fontSize: "0.65rem", fontWeight: 700 }}>
                  Aspect Ratio: {selectedAspect.value}
                </Typography>
              </Box>

              {/* Card Label & Arrow */}
              <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, p: 1.75 }}>
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.92rem",
                    lineHeight: 1.2,
                    mb: 0.4,
                    textShadow: "0 1px 4px rgba(0,0,0,0.6)",
                  }}
                >
                  {currentLabel}
                </Typography>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.3, color: currentAccent, fontSize: "0.72rem", fontWeight: 600 }}>
                  Shop Now <ArrowForwardRoundedIcon sx={{ fontSize: 12 }} />
                </Box>
              </Box>
            </Box>

            {/* Storefront Layout Recommendation Badge */}
            <Chip
              icon={<AspectRatioOutlinedIcon style={{ fontSize: 13, color: "#065f46" }} />}
              label={`Aspect Ratio: ${selectedAspect.value} (${selectedAspect.recSize})`}
              size="small"
              sx={{
                mt: 1.5,
                height: 24,
                bgcolor: "#eef7f4",
                color: "#065f46",
                fontWeight: 700,
                fontSize: "0.68rem",
                border: "1px solid #bbf7d0",
              }}
            />

            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, textAlign: "center", px: 1 }}>
              {selectedAspect.desc}. Reorder anytime by dragging in the collage preview.
            </Typography>
          </Paper>
        </Grid2>

        {/* Right Column: Form Controls */}
        <Grid2 xs={12} md={7}>
          <Grid2 container spacing={2}>
            {/* Property Label */}
            <Grid2 xs={12}>
              <TextField
                required
                fullWidth
                size="small"
                label="Property Category Label"
                placeholder="e.g. Body Detox Care"
                value={propertyContext.state.label || ""}
                onChange={(e) => {
                  propertyContext.setState({
                    ...propertyContext.state,
                    label: e.target.value,
                  });
                }}
              />
            </Grid2>


            {/* Required Aspect Ratio Selector */}
            <Grid2 xs={12}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.75}>
                REQUIRED ASPECT RATIO
              </Typography>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 0.75 }}>
                {ASPECT_RATIO_OPTIONS.map((opt) => {
                  const isSelected = currentAspectRatio === opt.value;
                  return (
                    <Chip
                      key={opt.value}
                      icon={<AspectRatioOutlinedIcon style={{ fontSize: 13, color: isSelected ? "#ffffff" : "#075e54" }} />}
                      label={`${opt.label} • ${opt.title}`}
                      onClick={() => {
                        propertyContext.setState({
                          ...propertyContext.state,
                          aspectRatio: opt.value,
                        });
                      }}
                      sx={{
                        cursor: "pointer",
                        fontWeight: isSelected ? 800 : 600,
                        fontSize: "0.72rem",
                        bgcolor: isSelected ? "#004d40" : "#ffffff",
                        color: isSelected ? "#ffffff" : "#1e293b",
                        border: `1.5px solid ${isSelected ? "#004d40" : "#cbd5e1"}`,
                        boxShadow: isSelected ? "0 2px 8px rgba(0, 77, 64, 0.25)" : "none",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: isSelected ? "#00382e" : "#f1f5f9",
                        },
                      }}
                    />
                  );
                })}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Recommended for <strong>{selectedAspect.value} ({selectedAspect.title})</strong>: <strong>{selectedAspect.recSize}</strong> — {selectedAspect.desc}.
              </Typography>
            </Grid2>

            {/* Image Upload Area */}
            <Grid2 xs={12}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                <Typography variant="subtitle2" color="text.secondary" fontWeight={700}>
                  Card Photography
                </Typography>
                <Chip
                  icon={<AspectRatioOutlinedIcon style={{ fontSize: 13, color: "#065f46" }} />}
                  label={`${selectedAspect.recSize} (${selectedAspect.value})`}
                  size="small"
                  sx={{
                    bgcolor: "#ecfdf5",
                    color: "#065f46",
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    border: "1px solid #a7f3d0",
                    height: 20,
                  }}
                />
              </Box>

              {/* Image Dimension Guidelines */}
              <Box
                sx={{
                  p: 1.5,
                  mb: 1.25,
                  borderRadius: 2,
                  bgcolor: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.2,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: "#ffffff",
                    color: "#059669",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    mt: 0.1,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                  }}
                >
                  <AspectRatioOutlinedIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mb: 0.35 }}>
                    <Typography variant="caption" fontWeight={800} sx={{ color: "#059669" }}>
                      Recommended for {selectedAspect.value}:
                    </Typography>
                    <Chip
                      label={selectedAspect.recSize}
                      size="small"
                      sx={{ height: 20, fontSize: "0.7rem", fontWeight: 800, bgcolor: "#ffffff", color: "#059669", border: "1px solid #a7f3d0" }}
                    />
                    <Chip
                      label={`${selectedAspect.title} ${selectedAspect.value}`}
                      size="small"
                      sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, bgcolor: "rgba(255,255,255,0.7)", color: "#059669" }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ display: "block", color: "#334155", fontSize: "0.74rem", lineHeight: 1.45 }}>
                    💡 <strong>Composition Guide:</strong> Upload {selectedAspect.title.toLowerCase()} photography ({selectedAspect.value}) with subjects centered. Uploaded images are auto-converted to WebP with crisp clarity and fast loading.
                  </Typography>
                </Box>
              </Box>

              {currentImg ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    bgcolor: "#ffffff",
                  }}
                >
                  <Box
                    component="img"
                    src={currentImg}
                    alt="Thumbnail"
                    sx={{ width: 48, height: 60, objectFit: "cover", borderRadius: 1 }}
                  />
                  <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                    <Typography variant="caption" fontWeight={600} display="block" noWrap>
                      {propertyContext.state.imageName || "Image active"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ready for {selectedAspect.value} ({selectedAspect.recSize}) display
                    </Typography>
                  </Box>
                  <IconButton component="label" size="small" sx={{ border: "1px solid #ccc" }}>
                    <EditIcon fontSize="small" />
                    <input type="file" accept="image/*" hidden disabled={isUploading || saving} onChange={handleImageChange} />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ width: "100%", height: 130 }}>
                  <FileUpload
                    className="full-width"
                    handleChange={handleImageChange}
                    upProgress={progress}
                    label={`Click or drag & drop property image (${selectedAspect.recSize})`}
                  />
                </Box>
              )}

              {isUploading && (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography variant="caption" color="text.secondary">
                    Converting & Uploading WebP… {progress}%
                  </Typography>
                </Box>
              )}
            </Grid2>

            {/* Direct Image URL fallback */}
            <Grid2 xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Or Image URL directly"
                placeholder="https://..."
                value={propertyContext.state.imageUrl || ""}
                onChange={(e) => {
                  propertyContext.setState({
                    ...propertyContext.state,
                    imageUrl: e.target.value,
                  });
                }}
                helperText={`Direct image URL fallback. Recommended for ${selectedAspect.value}: ${selectedAspect.recSize}.`}
              />
            </Grid2>

            {/* Homepage Status Toggle */}
            <Grid2 xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: "#f8fafc",
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                }}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="#1e293b">
                    Homepage Visibility
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {propertyContext.state.isActive !== false
                      ? "Visible on the storefront properties collage"
                      : "Hidden from homepage (saved as draft)"}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={propertyContext.state.isActive !== false}
                      onChange={(e) => {
                        propertyContext.setState({
                          ...propertyContext.state,
                          isActive: e.target.checked,
                        });
                      }}
                      color="success"
                    />
                  }
                  label={
                    <Typography fontWeight={700} fontSize="0.85rem" color={propertyContext.state.isActive !== false ? "#15803d" : "#64748b"}>
                      {propertyContext.state.isActive !== false ? "Active" : "Hidden"}
                    </Typography>
                  }
                  sx={{ mr: 0 }}
                />
              </Paper>
            </Grid2>
          </Grid2>
        </Grid2>

        {/* Modal Actions */}
        <Grid2
          xs={12}
          sx={{
            display: "flex",
            justifyContent: action === "EDIT" && onDelete ? "space-between" : "flex-end",
            alignItems: "center",
            gap: 1.5,
            pt: 2,
            borderTop: "1px solid #eef2f0",
          }}
        >
          {action === "EDIT" && onDelete && (
            <CustomButton
              variant="outlined"
              color="error"
              disabled={saving || isUploading}
              onClick={onDelete}
              startIcon={<DeleteOutlineRoundedIcon />}
              sx={{
                borderRadius: "10px",
                borderColor: "#fecaca",
                color: "#dc2626",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#ef4444",
                  bgcolor: "#fef2f2",
                },
              }}
            >
              Delete Property
            </CustomButton>
          )}
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <CustomButton
              variant="outlined"
              color="inherit"
              disabled={saving}
              onClick={hideModal}
              sx={{ borderRadius: "10px", borderColor: "#cbd5e1", color: "#475569", textTransform: "none" }}
            >
              Cancel
            </CustomButton>
            <CustomButton
              type="submit"
              variant="contained"
              disabled={isUploading || saving}
              sx={{
                bgcolor: "#004d40",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 800,
                px: 3,
                "&:hover": { bgcolor: "#05463f" },
              }}
            >
              {saving ? "Saving…" : action === "ADD" ? "Create Property" : "Save Changes"}
            </CustomButton>
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default AddProperty;
